import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  UploadCloud, FileSpreadsheet, FileText, CheckCircle,
  AlertCircle, X, Loader2, Import, Clock, Users, Truck,
  RefreshCw, Info, ShieldCheck, Search, Sparkles,
  CircleDot, Circle, CheckCircle2, ChevronRight, Edit2, CornerDownLeft
} from "lucide-react";
import { toast } from "sonner";

const SHIFTS = ["MAÑANA", "TARDE", "NOCHE"];
const shiftColor = (s) => s === "MAÑANA" ? "bg-amber-50 text-amber-700 border-amber-200" :
  s === "TARDE" ? "bg-orange-50 text-orange-700 border-orange-200" :
  "bg-slate-100 text-slate-600 border-slate-200";

const STAGE_LABELS = {
  start:            { icon: "init",   label: "Iniciando procesamiento" },
  claude_connect:   { icon: "ai",     label: "Conectando con Claude AI" },
  claude_sending:   { icon: "ai",     label: "Enviando documento" },
  claude_processing:{ icon: "ai",     label: "OCR en progreso" },
  claude_fallback:  { icon: "ai",     label: "Usando modo imagen" },
  img_convert:      { icon: "ai",     label: "Convirtiendo a imágenes" },
  claude_vision:    { icon: "ai",     label: "Analizando con Claude Vision" },
  claude_parsing:   { icon: "ai",     label: "Extrayendo entradas" },
  claude_done:      { icon: "done",   label: "OCR completado" },
  extraction_done:  { icon: "done",   label: "Extracción completada" },
  extraction_error: { icon: "error",  label: "Error en extracción" },
  validation_start: { icon: "valid",  label: "Validando personal" },
  validation_done:  { icon: "done",   label: "Validación completada" },
  validation_error: { icon: "error",  label: "Error en validación" },
  completed:        { icon: "done",   label: "¡Listo para importar!" },
  fatal_error:      { icon: "error",  label: "Error inesperado" },
};

const confidenceBadge = (nm) => {
  if (!nm) return null;
  const conf = nm.confidence;
  if (conf === "high") return (
    <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
      <ShieldCheck size={9} /> {nm.score}%
    </span>
  );
  if (conf === "medium") return (
    <span className="flex items-center gap-1 text-[10px] text-amber-600 font-semibold bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
      <AlertCircle size={9} /> {nm.score}%
    </span>
  );
  if (conf === "low") return (
    <span className="flex items-center gap-1 text-[10px] text-red-500 font-semibold bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
      <AlertCircle size={9} /> {nm.score}%
    </span>
  );
  if (conf === "manual") return (
    <span className="flex items-center gap-1 text-[10px] text-blue-600 font-semibold bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
      <Edit2 size={9} /> Manual
    </span>
  );
  return (
    <span className="flex items-center gap-1 text-[10px] text-slate-400 font-semibold bg-slate-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
      <X size={9} /> No encontrado
    </span>
  );
};

// ── OCR Progress Panel ────────────────────────────────────────────────────────
function OcrProgressPanel({ log, status, filename, isPdf }) {
  const elapsed = log.length > 0
    ? Math.round((new Date() - new Date(log[0].timestamp)) / 1000)
    : 0;

  const isCompleted = status === "completed";
  const isFailed = status === "failed";

  return (
    <div data-testid="ocr-progress-panel" className="card-white p-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCompleted ? "bg-emerald-50" : isFailed ? "bg-red-50" : "bg-blue-50"
        }`}>
          {isCompleted
            ? <CheckCircle2 size={20} className="text-emerald-600" />
            : isFailed
            ? <AlertCircle size={20} className="text-red-500" />
            : <Loader2 size={20} className="text-blue-600 animate-spin" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-800 truncate" title={filename}>{filename}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {isPdf ? (
              <span className="flex items-center gap-1 text-[11px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
                <Sparkles size={9} /> Claude Sonnet 4.6
              </span>
            ) : (
              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                Parser Excel directo
              </span>
            )}
            {!isCompleted && !isFailed && (
              <span className="text-xs text-slate-400">{elapsed}s transcurridos...</span>
            )}
            {isCompleted && (
              <span className="text-xs text-emerald-600 font-semibold">Completado</span>
            )}
            {isFailed && (
              <span className="text-xs text-red-500 font-semibold">Error en procesamiento</span>
            )}
          </div>
        </div>
      </div>

      {/* Stages */}
      {log.length > 0 && (
        <div className="space-y-1.5">
          {log.map((entry, i) => {
            const meta = STAGE_LABELS[entry.stage] || { icon: "default", label: entry.stage };
            const isLast = i === log.length - 1;
            const isDone = entry.status === "done";
            const isErr = entry.status === "error";
            const isActive = isLast && !isCompleted && !isFailed;

            return (
              <div
                key={i}
                data-testid={`log-stage-${entry.stage}`}
                className={`flex items-start gap-2.5 px-3 py-2 rounded-lg transition-all ${
                  isActive ? "bg-blue-50 border border-blue-100" :
                  isDone   ? "bg-emerald-50/60" :
                  isErr    ? "bg-red-50"  : "bg-slate-50"
                }`}
              >
                {/* Stage icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {isActive
                    ? <CircleDot size={13} className="text-blue-500 animate-pulse" />
                    : isDone
                    ? <CheckCircle2 size={13} className="text-emerald-500" />
                    : isErr
                    ? <AlertCircle size={13} className="text-red-400" />
                    : <Circle size={13} className="text-slate-300" />
                  }
                </div>

                {/* Message */}
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium leading-tight ${
                    isActive ? "text-blue-700" :
                    isDone   ? "text-emerald-700" :
                    isErr    ? "text-red-600" :
                    "text-slate-500"
                  }`}>
                    {entry.message}
                  </p>
                </div>

                {/* Timestamp */}
                <span className="text-[10px] text-slate-300 flex-shrink-0 mt-0.5">
                  {new Date(entry.timestamp).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              </div>
            );
          })}

          {/* Pulse row when still processing and no new log yet */}
          {!isCompleted && !isFailed && (
            <div className="flex items-center gap-2.5 px-3 py-2">
              <div className="flex gap-0.5">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-blue-400"
                    style={{ animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite` }}
                  />
                ))}
              </div>
              <span className="text-xs text-slate-400 italic">Esperando respuesta de Claude...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DocumentUpload() {
  const { authAxios } = useAuth();
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [history, setHistory] = useState([]);
  const [dragging, setDragging] = useState(false);

  // Upload flow state
  const [uploading, setUploading] = useState(false);

  // Processing state (background task)
  const [processingDocId, setProcessingDocId] = useState(null);
  const [processingLog, setProcessingLog] = useState([]);
  const [processingStatus, setProcessingStatus] = useState("processing");
  const [processingFilename, setProcessingFilename] = useState("");
  const [processingIsPdf, setProcessingIsPdf] = useState(false);

  // Current doc being previewed
  const [currentDoc, setCurrentDoc] = useState(null);
  const [currentDocId, setCurrentDocId] = useState(null);
  const [selectedShift, setSelectedShift] = useState("ALL");
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [deletingDocs, setDeletingDocs] = useState(false);

  useEffect(() => {
    authAxios.get("/contracts").then(r => {
      setContracts(r.data);
      if (r.data.length > 0) setContractId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!contractId) return;
    authAxios.get(`/documents?contract_id=${contractId}`).then(r => setHistory(r.data));
  }, [contractId, importResult, processingStatus]);

  // Polling for background task status
  useEffect(() => {
    if (!processingDocId) return;
    if (processingStatus === "completed" || processingStatus === "failed") return;

    pollRef.current = setInterval(async () => {
      try {
        const r = await authAxios.get(`/documents/${processingDocId}/status`);
        const doc = r.data;
        setProcessingLog(doc.processing_log || []);
        setProcessingStatus(doc.processing_status);

        if (doc.processing_status === "completed") {
          clearInterval(pollRef.current);
          setCurrentDocId(doc.id);
          setCurrentDoc(doc.extracted_data);
          const all = new Set((doc.extracted_data?.entries || []).map((_, i) => i));
          setSelectedEntries(all);
          if (doc.extracted_data?.total_extracted > 0) {
            toast.success(`Extraídas ${doc.extracted_data.total_extracted} entradas. Revisa y confirma.`);
          } else {
            toast.warning("No se encontraron entradas. Revisa el archivo.");
          }
        } else if (doc.processing_status === "failed") {
          clearInterval(pollRef.current);
          toast.error("Error procesando el documento. Consulta el log.");
        }
      } catch (_) {}
    }, 2000);

    return () => clearInterval(pollRef.current);
  }, [processingDocId, processingStatus]);

  const resetCurrent = () => {
    setCurrentDoc(null);
    setCurrentDocId(null);
    setProcessingDocId(null);
    setProcessingLog([]);
    setProcessingStatus("processing");
    setSelectedShift("ALL");
    setSelectedEntries(new Set());
    setImportResult(null);
    clearInterval(pollRef.current);
  };

  const toggleDocSelect = (id) => setSelectedDocs(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAllDocs = () => setSelectedDocs(s => s.length === history.length ? [] : history.map(d => d.id));
  const handleDeleteDocs = async () => {
    if (!selectedDocs.length) return;
    if (!window.confirm(`¿Eliminar ${selectedDocs.length} documento(s) seleccionado(s)?`)) return;
    setDeletingDocs(true);
    try {
      await authAxios.post("/documents/bulk-delete", { ids: selectedDocs });
      setSelectedDocs([]);
      toast.success(`${selectedDocs.length} documento(s) eliminado(s)`);
      authAxios.get(`/documents?contract_id=${contractId}`).then(r => setHistory(r.data));
    } catch {
      toast.error("Error al eliminar documentos");
    } finally {
      setDeletingDocs(false);
    }
  };

  // ── Upload ─────────────────────────────────────────────────────────────
  const handleUpload = async (file) => {
    if (!contractId || !file) return;
    resetCurrent();
    setUploading(true);

    const allowed = ["xlsx", "xlsm", "xls", "pdf"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error("Formato no soportado. Usa PDF, XLSX o XLSM.");
      setUploading(false);
      return;
    }

    const isPdf = ext === "pdf";
    setProcessingFilename(file.name);
    setProcessingIsPdf(isPdf);

    try {
      const form = new FormData();
      form.append("contract_id", contractId);
      form.append("file", file);
      const r = await authAxios.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setProcessingDocId(r.data.document_id);
      setProcessingStatus("processing");
      setProcessingLog([]);
      toast.info(isPdf ? "PDF enviado — Claude procesando con OCR..." : "Excel procesando...");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo el documento");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [contractId]);

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleUpload(file);
    e.target.value = "";
  };

  // ── Entry selection ────────────────────────────────────────────────────
  const visibleIndices = (currentDoc?.entries || [])
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => selectedShift === "ALL" || e.shift === selectedShift)
    .map(({ i }) => i);

  const visibleEntries = (currentDoc?.entries || []).filter(e =>
    selectedShift === "ALL" || e.shift === selectedShift
  );

  const allSelected = visibleIndices.length > 0 && visibleIndices.every(i => selectedEntries.has(i));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedEntries(prev => { const n = new Set(prev); visibleIndices.forEach(i => n.delete(i)); return n; });
    } else {
      setSelectedEntries(prev => { const n = new Set(prev); visibleIndices.forEach(i => n.add(i)); return n; });
    }
  };

  const toggleEntry = (i) => {
    setSelectedEntries(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  };

  // ── Import ─────────────────────────────────────────────────────────────
  const confirmImport = async () => {
    if (!currentDocId || selectedEntries.size === 0) return;
    setImporting(true);
    try {
      const entriesToImport = (currentDoc?.entries || []).filter((_, i) => selectedEntries.has(i));
      const r = await authAxios.post(`/documents/${currentDocId}/import`, {
        entries: entriesToImport,
        date: currentDoc.date,
        day_of_week: currentDoc.day_of_week
      });
      setImportResult(r.data);
      toast.success(`¡Importado! ${r.data.imported_entries} entradas → Parte del ${r.data.date}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const loadDoc = (doc) => {
    resetCurrent();
    setCurrentDocId(doc.id);
    setCurrentDoc(doc.extracted_data);
    const all = new Set((doc.extracted_data?.entries || []).map((_, i) => i));
    setSelectedEntries(all);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Inline name correction ────────────────────────────────────────────
  const [editingNameIdx, setEditingNameIdx] = useState(null);
  const [nameSearchQ, setNameSearchQ] = useState("");
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const nameSearchRef = useRef(null);

  const openNameEdit = (i, currentName) => {
    setEditingNameIdx(i);
    setNameSearchQ(currentName || "");
    setNameSuggestions([]);
    setTimeout(() => nameSearchRef.current?.focus(), 50);
  };

  useEffect(() => {
    if (editingNameIdx === null || !nameSearchQ.trim() || !contractId) return;
    const timer = setTimeout(async () => {
      try {
        const r = await authAxios.get(`/personnel/fuzzy-search?q=${encodeURIComponent(nameSearchQ)}&contract_id=${contractId}&limit=5`);
        setNameSuggestions(r.data.suggestions || []);
      } catch (_) {}
    }, 250);
    return () => clearTimeout(timer);
  }, [nameSearchQ, contractId, editingNameIdx]);

  const applyNameCorrection = (idx, suggestion) => {
    setCurrentDoc(prev => {
      const entries = [...(prev.entries || [])];
      entries[idx] = {
        ...entries[idx],
        personnel_name: suggestion.full_name,
        category: entries[idx].category || suggestion.category,
        name_match: {
          ...entries[idx].name_match,
          matched_name: suggestion.full_name,
          matched_id: suggestion.id,
          score: suggestion.score,
          confidence: suggestion.score >= 90 ? "high" : suggestion.score >= 70 ? "medium" : "low"
        }
      };
      return { ...prev, entries };
    });
    setEditingNameIdx(null);
    setNameSearchQ("");
    setNameSuggestions([]);
  };

  // Permite escribir el nombre directamente sobre la fila cuando no hay
  // ninguna sugerencia que coincida (ej. empleado nuevo aún no dado de alta).
  const applyFreeTextName = (idx, text) => {
    const clean = (text || "").trim();
    if (!clean) return;
    setCurrentDoc(prev => {
      const entries = [...(prev.entries || [])];
      entries[idx] = {
        ...entries[idx],
        personnel_name: clean,
        name_match: {
          matched_name: clean,
          matched_id: null,
          score: null,
          confidence: "manual"
        }
      };
      return { ...prev, entries };
    });
    setEditingNameIdx(null);
    setNameSearchQ("");
    setNameSuggestions([]);
    toast.success(`Nombre "${clean}" aplicado manualmente`);
  };

  const contract = contracts.find(c => c.id === contractId);
  const isProcessing = processingDocId && processingStatus === "processing";
  const showOcrPanel = processingDocId && !currentDoc;

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Importar Partes Históricos</h1>
          <p className="text-slate-400 text-sm mt-0.5">Sube Excel (.xlsm/.xlsx) o PDFs escaneados de partes de trabajo</p>
        </div>
        <select
          data-testid="upload-contract-select"
          value={contractId}
          onChange={e => { setContractId(e.target.value); resetCurrent(); }}
          className="text-sm border border-slate-200 rounded-xl px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20 font-semibold"
        >
          {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Info banner */}
      <div className="bg-[#E6F4EA] border border-[#007934]/20 rounded-xl p-4 flex gap-3 animate-fade-in-up">
        <Info size={16} className="text-[#007934] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#005c27] space-y-0.5">
          <p className="font-semibold">Formatos soportados:</p>
          <p>• <strong>Excel (.xlsm/.xlsx)</strong>: Plantilla "Parte de Trabajo" estándar de Valoriza — extracción directa, sin IA, 100% preciso</p>
          <p>• <strong>PDF escaneado</strong>: Partes mañana/tarde/noche — extracción OCR con <strong>Claude Sonnet 4.6</strong> (Anthropic), con trazabilidad en tiempo real</p>
        </div>
      </div>

      {/* Upload zone */}
      {!currentDoc && !uploading && !showOcrPanel && (
        <div
          data-testid="upload-dropzone"
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all
            ${dragging ? "border-[#007934] bg-[#E6F4EA]" : "border-slate-200 hover:border-[#007934]/40 hover:bg-slate-50"}
          `}
        >
          <UploadCloud size={40} className={`mx-auto mb-4 transition-colors ${dragging ? "text-[#007934]" : "text-slate-300"}`} />
          <p className="font-semibold text-slate-600 mb-1">Arrastra el archivo aquí o haz clic para seleccionar</p>
          <p className="text-sm text-slate-400">Excel (.xlsm, .xlsx) · PDF escaneado · PDF digital</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls,.pdf" className="hidden" onChange={onFileChange} />
        </div>
      )}

      {/* Upload spinner */}
      {uploading && (
        <div className="card-white p-8 text-center animate-fade-in-up">
          <Loader2 size={32} className="mx-auto mb-4 text-[#007934] animate-spin" />
          <p className="font-semibold text-slate-700">Subiendo archivo...</p>
        </div>
      )}

      {/* ── OCR Progress Panel (background task traceability) ── */}
      {showOcrPanel && (
        <div className="space-y-3">
          <OcrProgressPanel
            log={processingLog}
            status={processingStatus}
            filename={processingFilename}
            isPdf={processingIsPdf}
          />
          {processingStatus !== "failed" && (
            <p className="text-center text-xs text-slate-400">
              La página se actualizará automáticamente cuando termine el procesamiento
            </p>
          )}
          {processingStatus === "failed" && (
            <div className="flex justify-center">
              <button
                onClick={resetCurrent}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-all"
              >
                <RefreshCw size={14} /> Intentar con otro archivo
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Preview panel (after processing) ── */}
      {currentDoc && !uploading && (
        <div className="space-y-4 animate-fade-in-up">
          {/* Preview header */}
          <div className="card-white p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {currentDoc.source === "excel"
                    ? <FileSpreadsheet size={18} className="text-emerald-600" />
                    : <Sparkles size={18} className="text-purple-500" />}
                  <span className="font-bold text-slate-800">
                    {currentDoc.source === "excel" ? "Excel procesado" : "PDF procesado con IA"}
                  </span>
                  {currentDoc.ai_model && (
                    <span className="text-[11px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Sparkles size={9} /> {currentDoc.ai_model}
                    </span>
                  )}
                  <span className="text-xs bg-[#E6F4EA] text-[#007934] font-semibold px-2 py-0.5 rounded-full">
                    {currentDoc.total_extracted} entradas
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  Fecha: <strong>{currentDoc.date}</strong> · {currentDoc.day_of_week}
                  {contract && <> · Contrato: <strong>{contract.name}</strong></>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="upload-new-btn"
                  onClick={() => { resetCurrent(); }}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-600 transition-all"
                >
                  <RefreshCw size={14} /> Subir otro
                </button>
                {!importResult && (
                  <button
                    data-testid="confirm-import-btn"
                    onClick={confirmImport}
                    disabled={importing || selectedEntries.size === 0}
                    className="flex items-center gap-2 px-5 py-2 bg-[#007934] hover:bg-[#005c27] text-white rounded-xl text-sm font-semibold shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {importing ? <Loader2 size={15} className="animate-spin" /> : <Import size={15} />}
                    {importing ? "Importando..." : `Importar ${selectedEntries.size} entradas`}
                  </button>
                )}
              </div>
            </div>

            {/* Shift summary cards */}
            {currentDoc.shift_summary && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {Object.entries(currentDoc.shift_summary).map(([shift, stats]) => (
                  <button
                    key={shift}
                    data-testid={`shift-filter-${shift.toLowerCase()}`}
                    onClick={() => setSelectedShift(selectedShift === shift ? "ALL" : shift)}
                    className={`p-3 rounded-xl border-2 text-left transition-all
                      ${selectedShift === shift ? "border-[#007934] bg-[#E6F4EA]" : "border-slate-200 hover:border-slate-300"}
                    `}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded border ${shiftColor(shift)}`}>{shift}</span>
                      <span className="text-xs text-slate-400">{stats.entries} filas</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-600">
                      <span className="flex items-center gap-1"><Users size={10} /> {stats.staff}</span>
                      <span className="flex items-center gap-1"><Truck size={10} /> {stats.vehicles}</span>
                      <span className="flex items-center gap-1 text-emerald-600"><CheckCircle size={10} /> {stats.ok}</span>
                      {stats.ko > 0 && <span className="flex items-center gap-1 text-red-500"><AlertCircle size={10} /> {stats.ko}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Validation summary */}
            {currentDoc.validation_summary && (
              <div className="mt-4 p-3 rounded-xl border border-slate-200 bg-slate-50">
                <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                  <ShieldCheck size={13} className="text-[#007934]" /> Validación de personal contra base de datos
                </p>
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    {currentDoc.validation_summary.high} coincidencia alta (≥90%)
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    {currentDoc.validation_summary.medium} coincidencia media (70-89%)
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-red-500 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-red-400" />
                    {currentDoc.validation_summary.low_or_none} sin coincidencia clara
                  </span>
                  {currentDoc.validation_summary.no_name > 0 && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                      <span className="w-2 h-2 rounded-full bg-slate-300" />
                      {currentDoc.validation_summary.no_name} sin nombre
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Import success banner */}
          {importResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle size={20} className="text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800">
                  ¡Importación completada! {importResult.action === "merged" ? "Entradas añadidas al parte existente" : "Nuevo parte creado"}
                </p>
                <p className="text-sm text-emerald-600">
                  {importResult.imported_entries} entradas · Fecha: {importResult.date} · Cumplimiento: {importResult.completion_rate}%
                  {importResult.reliability && (
                    <> · Fiabilidad: <strong>{importResult.reliability === "high" ? "Alta" : importResult.reliability === "medium" ? "Media" : "Revisar"}</strong></>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Entries table */}
          <div className="card-white overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="rounded accent-[#007934]"
              />
              <span className="text-xs font-semibold text-slate-500">
                {selectedEntries.size} de {visibleIndices.length} seleccionadas
                {selectedShift !== "ALL" && ` (filtro: ${selectedShift})`}
              </span>
              {selectedShift !== "ALL" && (
                <button onClick={() => setSelectedShift("ALL")} className="text-xs text-[#007934] hover:underline ml-auto">
                  Ver todas
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="w-8 px-3 py-2"></th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Turno</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Servicio</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Matrícula</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Personal (validado)</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Sector / Ruta</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Real.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(currentDoc?.entries || []).map((entry, i) => {
                    if (selectedShift !== "ALL" && entry.shift !== selectedShift) return null;
                    const isSelected = selectedEntries.has(i);
                    const nm = entry.name_match;
                    const isEditing = editingNameIdx === i;
                    const rowBg = isSelected
                      ? nm?.confidence === "high" ? "bg-emerald-50/50"
                        : nm?.confidence === "medium" ? "bg-amber-50/40"
                        : nm?.confidence && nm.confidence !== "none" ? "bg-red-50/30"
                        : "bg-[#E6F4EA]"
                      : "hover:bg-slate-50";

                    return (
                      <tr key={i} data-testid={`entry-row-${i}`} className={`transition-colors ${rowBg}`}>
                        <td className="px-3 py-2 text-center" onClick={() => toggleEntry(i)}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggleEntry(i)} onClick={e => e.stopPropagation()} className="rounded accent-[#007934] cursor-pointer" />
                        </td>
                        <td className="px-3 py-2" onClick={() => toggleEntry(i)}>
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold whitespace-nowrap ${shiftColor(entry.shift)}`}>{entry.shift}</span>
                        </td>
                        <td className="px-3 py-2 max-w-xs" onClick={() => toggleEntry(i)}>
                          <p className="truncate font-medium text-slate-700" title={entry.service_type}>{entry.service_type}</p>
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-600 whitespace-nowrap" onClick={() => toggleEntry(i)}>
                          {entry.vehicle_plate || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 min-w-[220px] max-w-xs relative">
                          {isEditing ? (
                            <div className="relative z-20" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1 border border-[#007934] rounded-lg bg-white shadow-lg px-2 py-1">
                                <Search size={11} className="text-slate-400 flex-shrink-0" />
                                <input
                                  ref={nameSearchRef}
                                  value={nameSearchQ}
                                  onChange={e => setNameSearchQ(e.target.value)}
                                  onKeyDown={e => { if (e.key === "Enter") applyFreeTextName(i, nameSearchQ); }}
                                  className="flex-1 text-xs focus:outline-none min-w-0"
                                  placeholder="Escribe o busca un nombre..."
                                  data-testid={`name-freetext-input-${i}`}
                                />
                                <button onClick={() => { setEditingNameIdx(null); setNameSuggestions([]); }}><X size={11} className="text-slate-400" /></button>
                              </div>
                              {(nameSuggestions.length > 0 || nameSearchQ.trim()) && (
                                <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl z-30 mt-1 overflow-hidden">
                                  {nameSuggestions.map((s, si) => (
                                    <button key={si} data-testid={`name-suggestion-${si}`} onClick={() => applyNameCorrection(i, s)}
                                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#E6F4EA] text-left transition-colors border-b border-slate-100 last:border-0">
                                      <div>
                                        <p className="text-xs font-semibold text-slate-800">{s.full_name}</p>
                                        <p className="text-[10px] text-slate-400">{s.category}</p>
                                      </div>
                                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.score >= 90 ? "bg-emerald-100 text-emerald-700" : s.score >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600"}`}>
                                        {s.score}%
                                      </span>
                                    </button>
                                  ))}
                                  {nameSearchQ.trim() && (
                                    <button
                                      data-testid={`name-freetext-apply-${i}`}
                                      onClick={() => applyFreeTextName(i, nameSearchQ)}
                                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-blue-50 text-left transition-colors bg-blue-50/40"
                                    >
                                      <span className="text-xs text-blue-700">
                                        Usar <strong>"{nameSearchQ.trim()}"</strong> tal cual
                                      </span>
                                      <CornerDownLeft size={11} className="text-blue-400 flex-shrink-0" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="cursor-pointer group" onClick={() => openNameEdit(i, entry.personnel_name)} title="Clic para corregir el nombre">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className={`truncate font-medium text-sm
                                  ${nm?.confidence === "high" ? "text-slate-700" :
                                    nm?.confidence === "medium" ? "text-amber-700" :
                                    nm?.confidence === "low" ? "text-red-600" :
                                    nm?.confidence === "none" ? "text-red-600 line-through" :
                                    "text-slate-400 italic"}`}>
                                  {entry.personnel_name || "Sin nombre"}
                                </p>
                                {confidenceBadge(nm)}
                              </div>
                              {nm?.matched_name && nm.matched_name !== entry.personnel_name && (
                                <p className="text-[10px] text-emerald-600 mt-0.5">→ {nm.matched_name}</p>
                              )}
                              {entry.category && <p className="text-slate-400 text-[10px]">{entry.category}</p>}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 max-w-xs" onClick={() => toggleEntry(i)}>
                          <p className="truncate text-slate-500" title={entry.sector_route || ""}>{entry.sector_route || "—"}</p>
                        </td>
                        <td className="px-3 py-2" onClick={() => toggleEntry(i)}>
                          {entry.completed
                            ? <span className="text-emerald-500 font-bold">OK</span>
                            : <span className="text-red-500 font-bold">KO</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {visibleEntries.length === 0 && (
                <div className="text-center py-10 text-slate-400">
                  <FileText size={28} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No hay entradas para este filtro</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="card-white overflow-hidden animate-fade-in-up">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <Clock size={15} className="text-slate-400" /> Documentos procesados recientemente
          </h2>
          {selectedDocs.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-red-700">{selectedDocs.length} seleccionado(s)</span>
              <button data-testid="delete-docs-btn" onClick={handleDeleteDocs} disabled={deletingDocs}
                className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-60">
                {deletingDocs ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                Eliminar selección
              </button>
            </div>
          )}
        </div>
        {history.length === 0 ? (
          <div className="text-center py-10 text-slate-400">
            <UploadCloud size={28} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aún no has subido ningún documento</p>
          </div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th className="w-10">
                  <input type="checkbox" data-testid="select-all-docs"
                    checked={selectedDocs.length === history.length && history.length > 0}
                    onChange={toggleAllDocs} className="rounded accent-red-600" />
                </th>
                <th>Archivo</th>
                <th>Tipo</th>
                <th>Motor IA</th>
                <th>Fecha</th>
                <th>Entradas</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {history.map(doc => (
                <tr key={doc.id} data-testid={`doc-history-${doc.id}`}
                  className={selectedDocs.includes(doc.id) ? "bg-red-50/30" : ""}>
                  <td>
                    <input type="checkbox" checked={selectedDocs.includes(doc.id)}
                      onChange={() => toggleDocSelect(doc.id)} className="rounded accent-red-600" />
                  </td>
                  <td className="font-medium text-slate-700 max-w-xs">
                    <p className="truncate" title={doc.filename}>{doc.filename}</p>
                  </td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${doc.file_type === "excel" ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-blue-600"}`}>
                      {doc.file_type === "excel" ? "Excel" : "PDF"}
                    </span>
                  </td>
                  <td>
                    {doc.file_type === "pdf" ? (
                      <span className="text-[11px] flex items-center gap-1 text-purple-600 font-medium">
                        <Sparkles size={10} /> {doc.extracted_data?.ai_model || "Claude Sonnet 4.6"}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">Parser directo</span>
                    )}
                  </td>
                  <td className="text-slate-500 text-xs">
                    {new Date(doc.upload_date).toLocaleDateString("es-ES")}
                  </td>
                  <td className="font-semibold text-slate-700">
                    {doc.extracted_data?.total_extracted ?? "—"}
                  </td>
                  <td>
                    {doc.imported
                      ? <span className="badge-ok text-xs">Importado</span>
                      : doc.processing_status === "completed"
                        ? <span className="text-xs text-amber-600 font-medium">Pendiente</span>
                        : doc.processing_status === "failed"
                          ? <span className="text-xs text-red-500 font-medium">Error</span>
                          : <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Procesando</span>}
                  </td>
                  <td>
                    {doc.extracted_data?.total_extracted > 0 && !doc.imported && (
                      <button data-testid={`review-doc-${doc.id}`} onClick={() => loadDoc(doc)}
                        className="text-xs text-[#007934] hover:underline font-semibold">
                        Revisar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
